import { useState } from 'react';
import { Wand2, AlertTriangle, CheckCircle, XCircle, RefreshCw, Shield, Zap, Bug, Code, FileCode } from 'lucide-react';

export const AIToolsTab = ({
  isArabic,
  onRunDiagnosis,
  diagnosisReport,
  diagnosisLoading
}) => {
  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900">
          {isArabic ? 'أدوات الذكاء الاصطناعي' : 'AI Tools'}
        </h2>
      </div>

      {/* Code Diagnosis Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-4xl">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Wand2 className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isArabic ? 'فحص وإصلاح الكود بالذكاء الاصطناعي' : 'AI-Powered Code Diagnosis & Repair'}
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              {isArabic 
                ? 'يقوم GPT-5.2 بفحص الكود تلقائياً للعثور على الأخطاء الأمنية والأداء والجودة'
                : 'GPT-5.2 automatically scans code for security, performance, and quality issues'}
            </p>
          </div>
        </div>

        {/* Diagnosis Button */}
        <button
          onClick={onRunDiagnosis}
          disabled={diagnosisLoading}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 w-full justify-center mb-6"
          data-testid="run-diagnosis-btn"
        >
          {diagnosisLoading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              {isArabic ? 'جاري الفحص بالذكاء الاصطناعي...' : 'AI Analyzing Code...'}
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              {isArabic ? 'بدء الفحص الذكي' : 'Run AI Diagnosis'}
            </>
          )}
        </button>

        {/* Progress Bar */}
        {diagnosisLoading && diagnosisReport && (
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-500 mb-2">
              <span>{isArabic ? 'التقدم' : 'Progress'}</span>
              <span>{diagnosisReport.progress || 0}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div 
                className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${diagnosisReport.progress || 0}%` }}
              />
            </div>
            <div className="mt-2 text-xs text-slate-400 text-center">
              {diagnosisReport.progress < 30 && (isArabic ? 'جمع الملفات...' : 'Collecting files...')}
              {diagnosisReport.progress >= 30 && diagnosisReport.progress < 50 && (isArabic ? 'فحص الأخطاء النحوية...' : 'Running linters...')}
              {diagnosisReport.progress >= 50 && diagnosisReport.progress < 80 && (isArabic ? 'تحليل AI للكود...' : 'AI analyzing code...')}
              {diagnosisReport.progress >= 80 && (isArabic ? 'فحص قاعدة البيانات...' : 'Checking database...')}
            </div>
          </div>
        )}

        {/* Results */}
        {diagnosisReport && diagnosisReport.status === 'completed' && (
          <div className="space-y-6">
            {/* Health Score */}
            {diagnosisReport.summary?.health_score && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">
                      {isArabic ? 'درجة صحة الكود' : 'Code Health Score'}
                    </h4>
                    <p className="text-sm text-slate-500">
                      {isArabic ? `تم تحليل ${diagnosisReport.summary.files_analyzed || 0} ملف` : `${diagnosisReport.summary.files_analyzed || 0} files analyzed`}
                    </p>
                  </div>
                  <div className={`text-4xl font-bold ${
                    diagnosisReport.summary.health_score >= 80 ? 'text-green-600' :
                    diagnosisReport.summary.health_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {diagnosisReport.summary.health_score}%
                  </div>
                </div>
              </div>
            )}

            {/* AI Summary */}
            {diagnosisReport.summary?.ai_summary && (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <Wand2 className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">
                      {isArabic ? 'ملخص التحليل الذكي' : 'AI Analysis Summary'}
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {diagnosisReport.summary.ai_summary}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <SummaryCard
                icon={<FileCode className="w-5 h-5" />}
                label={isArabic ? 'إجمالي' : 'Total'}
                value={diagnosisReport.summary?.total_issues || 0}
                color="slate"
              />
              <SummaryCard
                icon={<XCircle className="w-5 h-5" />}
                label={isArabic ? 'خطير' : 'Critical'}
                value={diagnosisReport.summary?.errors || 0}
                color="red"
              />
              <SummaryCard
                icon={<AlertTriangle className="w-5 h-5" />}
                label={isArabic ? 'تحذيرات' : 'Warnings'}
                value={diagnosisReport.summary?.warnings || 0}
                color="yellow"
              />
              <SummaryCard
                icon={<CheckCircle className="w-5 h-5" />}
                label={isArabic ? 'معلومات' : 'Info'}
                value={diagnosisReport.summary?.info || 0}
                color="blue"
              />
            </div>

            {/* Issues List */}
            {diagnosisReport.issues && diagnosisReport.issues.length > 0 ? (
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900">
                  {isArabic ? 'المشاكل المكتشفة' : 'Issues Found'}
                </h4>
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {diagnosisReport.issues.map((issue, idx) => (
                    <IssueItem key={idx} issue={issue} isArabic={isArabic} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-xl">
                <CheckCircle className="w-5 h-5" />
                <span>{isArabic ? 'لا توجد مشاكل! الكود سليم.' : 'No issues found! Code is healthy.'}</span>
              </div>
            )}

            {/* Run Again Button */}
            <button
              onClick={onRunDiagnosis}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl text-slate-700 hover:bg-slate-200 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {isArabic ? 'فحص جديد' : 'Run Again'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ icon, label, value, color }) => {
  const colors = {
    red: 'bg-red-50 text-red-600 border-red-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    slate: 'bg-slate-50 text-slate-600 border-slate-200'
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-2xl font-bold">{value}</span>
      </div>
      <span className="text-sm">{label}</span>
    </div>
  );
};

const IssueItem = ({ issue, isArabic }) => {
  const severityConfig = {
    high: {
      bg: 'bg-red-50 border-red-200',
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      label: isArabic ? 'خطير' : 'Critical'
    },
    medium: {
      bg: 'bg-yellow-50 border-yellow-200',
      icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
      label: isArabic ? 'متوسط' : 'Warning'
    },
    low: {
      bg: 'bg-blue-50 border-blue-200',
      icon: <CheckCircle className="w-5 h-5 text-blue-500" />,
      label: isArabic ? 'منخفض' : 'Info'
    }
  };

  const typeIcons = {
    Security: <Shield className="w-4 h-4" />,
    Performance: <Zap className="w-4 h-4" />,
    Bug: <Bug className="w-4 h-4" />,
    BestPractice: <Code className="w-4 h-4" />,
    Quality: <FileCode className="w-4 h-4" />
  };

  const config = severityConfig[issue.severity] || severityConfig.low;

  return (
    <div className={`p-4 rounded-xl border ${config.bg}`}>
      <div className="flex items-start gap-3">
        {config.icon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium px-2 py-0.5 bg-white rounded-full border">
              {typeIcons[issue.type]} {issue.type || 'Issue'}
            </span>
            {issue.file && (
              <span className="text-xs text-slate-500 truncate">
                {issue.file}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-900 font-medium">{issue.message}</p>
          {issue.suggestion && (
            <p className="text-xs text-slate-500 mt-2">
              <span className="font-medium">{isArabic ? 'اقتراح:' : 'Suggestion:'}</span> {issue.suggestion}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIToolsTab;
